import React, { useEffect, useRef, useState } from "react";
import { X, UserPlus, ChevronDown } from "lucide-react";
import { Country } from "country-state-city";
import { toast } from "sonner";
import useRequest from "../../../components/hook/use-req";
import {
  BUSINESS_TYPE_OPTIONS,
  CreateResellerPayload,
  DAILY_VOLUME_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
} from "./types";

// ─── Dial code list — same pattern as crm-pages/team/index.tsx and
// components/crm/customer/update-customer.tsx, duplicated locally per this
// codebase's existing convention rather than shared. ───────────────────────
const normalizeDialCode = (phoneCode?: string | number | null) => {
  if (!phoneCode) return "+234";
  const code = String(phoneCode);
  return code.startsWith("+") ? code : `+${code}`;
};

const countryCodeOptions = Country.getAllCountries()
  .map((country) => ({
    value: normalizeDialCode(country.phonecode),
    label: `${normalizeDialCode(country.phonecode)} ${country.isoCode}`,
    isoCode: country.isoCode,
  }))
  .sort((a, b) => a.value.localeCompare(b.value));

const getCountryByDialCode = (dialCode?: string | number | null) =>
  countryCodeOptions.find((c) => c.value === normalizeDialCode(dialCode));

// Flag + dial code dropdown button with a searchless list.
const CountryCodeSelect: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = getCountryByDialCode(value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex w-full items-center gap-1 px-2 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100"
      >
        {selected && (
          <img
            src={`https://flagcdn.com/24x18/${selected.isoCode.toLowerCase()}.png`}
            alt=""
            className="h-3.5 w-5 rounded-sm object-cover shrink-0"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        )}
        <span>{value || "+234"}</span>
        <ChevronDown size={14} className={`ml-auto text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-56 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          {countryCodeOptions.map((option) => (
            <button
              key={option.isoCode}
              type="button"
              onClick={() => { onChange(option.value); setOpen(false); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                option.value === value ? "bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400" : "text-gray-700 dark:text-gray-300"
              }`}
            >
              <img
                src={`https://flagcdn.com/24x18/${option.isoCode.toLowerCase()}.png`}
                alt=""
                className="h-3.5 w-5 rounded-sm object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const EMPTY_FORM: CreateResellerPayload = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  organization_name: "",
  country_code: 234,
  business_type: "corporation",
  expected_daily_volume: "10k-100k",
  business_address: "",
  initial_credit_amount: 0,
  preferred_payment: "bank_transfer",
  consent_terms: false,
  consent_antispam: false,
};

const inputClass =
  "w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500";

// POST /admin/resellers — creates the reseller's Organization + User
// (role=reseller_admin) + Reseller row, credits the wallet, and emails a
// temp password (per the backend collection's own description).
const CreateResellerModal: React.FC<{ isOpen: boolean; onClose: () => void; onCreated: () => void }> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const token = localStorage.getItem("authToken");
  const [form, setForm] = useState<CreateResellerPayload>(EMPTY_FORM);
  // Dial-code display value (e.g. "+234") for the dropdown — form.country_code
  // itself stays the plain numeric value the API expects (e.g. 234).
  const [countryCode, setCountryCode] = useState("+234");
  const [submitting, setSubmitting] = useState(false);

  const { makeRequest: createReseller } = useRequest("/admin/resellers", "POST", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: getCurrentUser } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  // Default to the logged-in admin's own country (same source
  // crm-pages/team/index.tsx uses) rather than hardcoding Nigeria.
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      const [response] = await getCurrentUser();
      const code = response?.payload?.country_code;
      if (code) setCountryCode(normalizeDialCode(code));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const update = <K extends keyof CreateResellerPayload>(key: K, value: CreateResellerPayload[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleClose = () => {
    if (submitting) return;
    setForm(EMPTY_FORM);
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.organization_name.trim()) {
      toast.error("Fill in the required fields.");
      return;
    }
    if (!form.consent_terms || !form.consent_antispam) {
      toast.error("Both consent checkboxes are required.");
      return;
    }
    setSubmitting(true);
    const [response, status] = await createReseller({
      ...form,
      country_code: Number(countryCode.replace(/^\+/, "")) || 234,
      initial_credit_amount: Number(form.initial_credit_amount) || 0,
    });
    setSubmitting(false);
    if (status === 201 || (status >= 200 && status < 300)) {
      toast.success("Reseller created — a temp password has been emailed to them.");
      setForm(EMPTY_FORM);
      onCreated();
      onClose();
    } else {
      toast.error(response?.detail || response?.message || "Failed to create reseller.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center shrink-0">
              <UserPlus size={18} className="text-blue-600 dark:text-blue-400" />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">Create Reseller</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Sets up their organization, wallet and login</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-[#7B7B7B] font-semibold hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">First Name</label>
              <input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Last Name</label>
              <input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Email</label>
              <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Phone Number</label>
              <div className="flex gap-2">
                <div className="w-20 shrink-0">
                  <CountryCodeSelect value={countryCode} onChange={setCountryCode} />
                </div>
                <div className="flex-1">
                  <input
                    value={form.phone_number}
                    onChange={(e) => update("phone_number", e.target.value)}
                    placeholder="8012345678"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Organization Name</label>
            <input value={form.organization_name} onChange={(e) => update("organization_name", e.target.value)} className={inputClass} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Business Type</label>
              <select value={form.business_type} onChange={(e) => update("business_type", e.target.value as any)} className={inputClass}>
                {BUSINESS_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Expected Daily Volume</label>
              <select
                value={form.expected_daily_volume}
                onChange={(e) => update("expected_daily_volume", e.target.value as any)}
                className={inputClass}
              >
                {DAILY_VOLUME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Business Address</label>
            <textarea
              value={form.business_address}
              onChange={(e) => update("business_address", e.target.value)}
              rows={2}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Initial Credit Amount</label>
              <input
                type="number"
                min={0}
                value={form.initial_credit_amount}
                onChange={(e) => update("initial_credit_amount", Number(e.target.value) as any)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Preferred Payment</label>
              <select
                value={form.preferred_payment}
                onChange={(e) => update("preferred_payment", e.target.value as any)}
                className={inputClass}
              >
                {PAYMENT_METHOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2 pt-1">
            <label className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={form.consent_terms}
                onChange={(e) => update("consent_terms", e.target.checked)}
                className="mt-0.5"
              />
              I confirm this reseller has agreed to the terms of service.
            </label>
            <label className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={form.consent_antispam}
                onChange={(e) => update("consent_antispam", e.target.checked)}
                className="mt-0.5"
              />
              I confirm this reseller has agreed to the anti-spam policy.
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 px-5 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-800 shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-colors"
          >
            {submitting ? "Creating…" : "Create Reseller"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateResellerModal;
